import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdSearch, 
  MdError, 
  MdFilterList, 
  MdInventory,
  MdVisibility,
  MdClose,
  MdArrowBack,
  MdDelete,
  MdEdit,
  MdDownload
} from "react-icons/md";
import { useNavigate } from 'react-router-dom';
import { tokenManager } from '../../../utils/tokenManager';

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const FieldAnalysisRecords = () => {
  const navigate = useNavigate();
  
  const [records, setRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    region: '',
    provinceCity: '',
    analyst: ''
  });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const itemsPerPage = 10;

  // Fetch data from API
  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }
      
      const response = await fetch('https://usibras-web-backend.onrender.com/api/field-analysis', {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.data) {
        setRecords(result.data);
      } else {
        setRecords([]);
      }
    } catch (error) {
      console.error('Error fetching field analysis records:', error);
      setError('Failed to load field analysis records. Please try again later.');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  // Apply filters and search
  const filteredData = records.filter(item => {
    const searchTermLower = searchTerm.toLowerCase();
    const searchableValues = [
      item.order_no,
      item.region,
      item.province_city,
      item.name_agent,
      item.truck_no,
      item.analyst_name,
      item.date
    ].filter(Boolean);

    const matchesSearch = searchTermLower === '' || 
      searchableValues.some(value => 
        value.toString().toLowerCase().includes(searchTermLower)
      );

    const itemDate = new Date(item.date);
    const startDate = filters.startDate ? new Date(filters.startDate) : null;
    const endDate = filters.endDate ? new Date(filters.endDate) : null;

    const matchesDate = (!startDate || itemDate >= startDate) &&
                       (!endDate || itemDate <= endDate);
    
    const matchesRegion = !filters.region || item.region === filters.region;
    const matchesProvinceCity = !filters.provinceCity || item.province_city === filters.provinceCity;
    const matchesAnalyst = !filters.analyst || item.analyst_name === filters.analyst;

    return matchesSearch && matchesDate && matchesRegion && matchesProvinceCity && matchesAnalyst;
  });

  // Calculate summary statistics
  const totalRecords = filteredData.length;
  const averageTDR = filteredData.length > 0 
    ? (filteredData.reduce((sum, item) => sum + parseFloat(item.tdr_percent || 0), 0) / filteredData.length).toFixed(1)
    : '0.0';
  const averageMoisture = filteredData.length > 0 
    ? (filteredData.reduce((sum, item) => sum + parseFloat(item.moisture_percent || 0), 0) / filteredData.length).toFixed(1)
    : '0.0';

  // Get unique values for filters
  const uniqueRegions = [...new Set(records.map(item => item.region))].filter(Boolean);
  const uniqueProvinceCities = [...new Set(records.map(item => item.province_city))].filter(Boolean);
  const uniqueAnalysts = [...new Set(records.map(item => item.analyst_name))].filter(Boolean);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const TABLE_HEAD = ["ID", "Date", "Truck Order No", "RCN Type", "Province/City", "Agent", "Truck No", "Bags", "TDR %", "Moisture %", "Analyst", "Actions"];

  // Format date for display
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString || 'N/A';
    }
  };

  // Format date for input field
  const formatDateForInput = (dateString) => {
    try {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  };

  // Handle view details
  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setShowModal(true);
  };

  // Handle delete
  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recordToDelete) return;

    try {
      setDeleting(true);
      
      // Get authorization header
      const authHeader = tokenManager.getAuthHeader();
      if (!authHeader) {
        setError('Authentication token not found. Please login again.');
        setDeleting(false);
        return;
      }
      
      const response = await fetch(`https://usibras-web-backend.onrender.com/api/field-analysis/${recordToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setRecords(prev => prev.filter(record => record.id !== recordToDelete.id));
      setShowDeleteModal(false);
      setRecordToDelete(null);
      
    } catch (error) {
      console.error('Error deleting record:', error);
      setError('Failed to delete record. Please try again later.');
    } finally {
      setDeleting(false);
    }
  };

  // Handle download
  const handleDownloadData = async () => {
    try {
      setDownloading(true);
      
      const XLSX = await import('xlsx');
      
      const exportData = records.map(record => ({
        'ID': record.id,
        'Date': formatDate(record.date),
        'Order No': record.order_no || 'N/A',
        'Region': record.region || 'N/A',
        'Province/City': record.province_city || 'N/A',
        'Name/Agent': record.name_agent,
        'Truck No': record.truck_no || 'N/A',
        'Bags (No)': record.bags_no || 'N/A',
        'Driver Name': record.driver_name || 'N/A',
        'Driver Contact': record.driver_contact || 'N/A',
        'Outturn (LBS)': record.outturn_lbs || 'N/A',
        'Departure': record.departure ? 'Yes' : 'No',
        'Nut Count': record.nut_count || 'N/A',
        'Good (g)': record.good_g || 'N/A',
        'Spotted (g)': record.spotted_g || 'N/A',
        'Immature (g)': record.immature_g || 'N/A',
        'Impurities (g)': record.impurities_g || 'N/A',
        'Bored (g)': record.bored_g || 'N/A',
        'Rotten (g)': record.rotten_g || 'N/A',
        'Void (g)': record.void_g || 'N/A',
        'Oil (g)': record.oil_g || 'N/A',
        'Shell (g)': record.shell_g || 'N/A',
        'Total (g)': record.total_g || 'N/A',
        'TDR %': record.tdr_percent || 'N/A',
        'Moisture %': record.moisture_percent || 'N/A',
        'Additional Comments': record.additional_comments || 'N/A',
        'Agent Sign': record.agent_sign || 'N/A',
        'Agent Date': formatDate(record.agent_date),
        'Analyst Name': record.analyst_name || 'N/A',
        'Quality Rating': record.quality_rating || 'N/A',
        'Defective Rate': record.defective_rate || 'N/A'
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15)
      }));
      worksheet['!cols'] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Field Analysis Records');

      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `field_analysis_records_${currentDate}.xlsx`;

      XLSX.writeFile(workbook, filename);

    } catch (error) {
      console.error('Error downloading data:', error);
      setError('Failed to download data. Please try again later.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-5 h-full">
      <Card extra={"w-full h-full p-4"}>
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <button
              onClick={() => navigate(-1)}
              className="flex w-fit items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
            >
              <MdArrowBack className="h-5 w-5" />
              Back
            </button>
            <div>
              <h4 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                Field Analysis Records
              </h4>
              <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                View and manage all field analysis records
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleDownloadData}
              className="flex w-fit items-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600"
              disabled={downloading || loading || records.length === 0}
            >
              {downloading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <MdDownload className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">{downloading ? 'Downloading...' : 'Download Excel'}</span>
            </button>
            <button
              onClick={fetchRecords}
              className="flex w-fit items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              disabled={loading}
            >
              <MdSearch className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-6">
          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-blue-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdInventory className="h-5 w-5 text-blue-600 dark:text-blue-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Total Records</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{totalRecords}</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-purple-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdInventory className="h-5 w-5 text-purple-600 dark:text-purple-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Avg TDR</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{averageTDR}%</p>
              </div>
            </div>
          </Card>

          <Card extra="p-3 sm:p-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="rounded-full bg-green-50 p-2 dark:bg-navy-700 sm:p-3">
                <MdInventory className="h-5 w-5 text-green-600 dark:text-green-400 sm:h-6 sm:w-6" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Avg Moisture</p>
                <p className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">{averageMoisture}%</p>
              </div>
            </div>
          </Card>

        </div>

        {/* Filters */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-3 dark:border-navy-700 dark:bg-navy-800 sm:p-4">
          <div className="mb-4 flex items-center gap-2">
            <MdFilterList className="h-4 w-4 text-gray-500 sm:h-5 sm:w-5" />
            <h3 className="text-base font-semibold text-gray-700 dark:text-white sm:text-lg">Filters</h3>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5 sm:gap-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({...filters, startDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({...filters, endDate: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">RCN Type</label>
              <select
                value={filters.region}
                onChange={(e) => setFilters({...filters, region: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="">All RCN Types</option>
                {uniqueRegions.map((region) => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Province/City</label>
              <select
                value={filters.provinceCity}
                onChange={(e) => setFilters({...filters, provinceCity: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="">All Provinces/Cities</option>
                {uniqueProvinceCities.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400 sm:mb-2 sm:text-sm">Analyst</label>
              <select
                value={filters.analyst}
                onChange={(e) => setFilters({...filters, analyst: e.target.value})}
                className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs dark:border-navy-600 dark:bg-navy-700 sm:px-3 sm:py-2 sm:text-sm"
              >
                <option value="">All Analysts</option>
                {uniqueAnalysts.map((analyst) => (
                  <option key={analyst} value={analyst}>{analyst}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3 flex justify-end sm:mt-4">
            <button
              onClick={() => setFilters({startDate: '', endDate: '', region: '', provinceCity: '', analyst: ''})}
              className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 sm:px-4 sm:py-2 sm:text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full sm:w-auto">
            <div className="flex h-8 items-center rounded-full bg-white px-3 dark:bg-navy-900 sm:h-10 sm:px-4">
              <span className="text-xl">
                <MdSearch className="h-4 w-4 text-gray-400 sm:h-5 sm:w-5" />
              </span>
              <input
                type="text"
                placeholder="Search..."
                className="ml-2 w-full bg-transparent text-xs font-medium text-gray-600 outline-none dark:text-white sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Loader />
        ) : error ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-red-500">
                <MdError className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                Oops! Something went wrong
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
              <button
                onClick={fetchRecords}
                className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : records.length === 0 ? (
          <div className="flex h-[400px] items-center justify-center">
            <div className="text-center">
              <div className="mb-4 text-5xl text-gray-400">
                <MdInventory className="mx-auto h-16 w-16" />
              </div>
              <p className="text-xl font-semibold text-gray-700 dark:text-white">
                No records found
              </p>
              <p className="mt-2 text-gray-500 dark:text-gray-400">
                No field analysis records are available at the moment.
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr>
                  {TABLE_HEAD.map((head, index) => (
                    <th
                      key={index}
                      className="border-b border-gray-200 pb-2 pr-2 text-start dark:!border-navy-700 sm:pb-[10px] sm:pr-4"
                    >
                      <p className="text-xs font-bold tracking-wide text-gray-800 dark:text-white sm:text-sm">
                        {head}
                      </p>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => (
                  <tr key={item.id}>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.id}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {formatDate(item.date)}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.order_no || 'N/A'}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.region || 'N/A'}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.province_city || 'N/A'}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.name_agent}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.truck_no || 'N/A'}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.bags_no || 'N/A'}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.tdr_percent || 'N/A'}%
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.moisture_percent || 'N/A'}%
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <p className="text-xs font-bold text-navy-700 dark:text-white sm:text-sm">
                        {item.analyst_name || 'N/A'}
                      </p>
                    </td>
                    <td className="pt-2 pb-3 pr-2 sm:pt-[14px] sm:pb-[18px] sm:pr-4">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button
                          onClick={() => handleViewDetails(item)}
                          className="rounded-lg bg-blue-100 p-1.5 text-blue-600 hover:bg-blue-200 dark:bg-navy-700 dark:text-blue-400 dark:hover:bg-navy-600 sm:p-2"
                          title="View Details"
                        >
                          <MdVisibility className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(item)}
                          className="rounded-lg bg-red-100 p-1.5 text-red-600 hover:bg-red-200 dark:bg-navy-700 dark:text-red-400 dark:hover:bg-navy-600 sm:p-2"
                          title="Delete Record"
                        >
                          <MdDelete className="h-3 w-3 sm:h-4 sm:w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredData.length > 0 && (
          <div className="mt-4 flex flex-col gap-4 sm:mt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs font-medium text-navy-700 dark:text-white sm:text-sm">
              Showing {paginatedData.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} entries
            </p>
            <div className="flex flex-wrap gap-1 sm:gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === 1
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">First</span>
                <span className="sm:hidden">1</span>
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === 1
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">‹</span>
              </button>
              
              {[...Array(Math.min(3, totalPages))].map((_, index) => {
                let pageNum;
                if (totalPages <= 3) {
                  pageNum = index + 1;
                } else if (currentPage <= 2) {
                  pageNum = index + 1;
                } else if (currentPage >= totalPages - 1) {
                  pageNum = totalPages - 2 + index;
                } else {
                  pageNum = currentPage - 1 + index;
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                      currentPage === pageNum
                        ? "bg-brand-700 text-white"
                        : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === totalPages || totalPages === 0
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">›</span>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`linear rounded-md px-2 py-1 text-xs font-medium transition duration-200 sm:px-3 sm:text-sm ${
                  currentPage === totalPages || totalPages === 0
                    ? "cursor-not-allowed bg-gray-300 text-gray-500"
                    : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <span className="hidden sm:inline">Last</span>
                <span className="sm:hidden">{totalPages}</span>
              </button>
            </div>
          </div>
        )}

        {/* Details Modal */}
        {showModal && selectedRecord && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-4 dark:bg-navy-800 sm:p-6">
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600 sm:mb-6 sm:pb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    Field Analysis Details
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    ID: {selectedRecord.id} | TruckOrder No: {selectedRecord.order_no || 'N/A'}
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-white sm:p-2"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
                {/* General Information */}
                <div className="space-y-3 sm:space-y-4">
                  <h4 className="text-base font-semibold text-navy-700 dark:text-white sm:text-lg">General Information</h4>
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Date:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{formatDate(selectedRecord.date)}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Truck Order No:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.order_no || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">RCN Type:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.region || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Province/City:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.province_city || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Name/Agent:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.name_agent}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Truck No:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.truck_no || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Bags (No):</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.bags_no || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Driver Name:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.driver_name || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Driver Contact:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.driver_contact || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Outturn (LBS):</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.outturn_lbs || 'N/A'}</span>
                    </div>
                    <div className="flex flex-col gap-1 sm:flex-row sm:justify-between">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 sm:text-sm">Departure:</span>
                      <span className="text-xs font-semibold text-navy-700 dark:text-white sm:text-sm">{selectedRecord.departure ? 'Yes' : 'No'}</span>
                    </div>
                  </div>
                </div>

                {/* Analysis Data */}
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Analysis Data</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Nut Count:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.nut_count || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">TDR %:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.tdr_percent || 'N/A'}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Moisture %:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.moisture_percent || 'N/A'}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Quality Rating:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.quality_rating || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Defective Rate:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.defective_rate || 'N/A'}%</span>
                    </div>
                  </div>
                </div>

                {/* Weight Breakdown */}
                <div className="space-y-4 lg:col-span-2">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Weight Breakdown (g)</h4>
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Good</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.good_g || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Spotted</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.spotted_g || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Immature</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.immature_g || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Impurities</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.impurities_g || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Bored</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.bored_g || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Rotten</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.rotten_g || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Void</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.void_g || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Oil</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.oil_g || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 dark:bg-navy-700">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Shell</p>
                      <p className="text-lg font-bold text-navy-700 dark:text-white">{selectedRecord.shell_g || 'N/A'}</p>
                    </div>
                    <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                      <p className="text-xs font-medium text-blue-600 dark:text-blue-400">Total</p>
                      <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{selectedRecord.total_g || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Signatures */}
                <div className="space-y-4 lg:col-span-2">
                  <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Signatures</h4>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Agent Sign:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.agent_sign || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Agent Date:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{formatDate(selectedRecord.agent_date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Analyst Name:</span>
                      <span className="text-sm font-semibold text-navy-700 dark:text-white">{selectedRecord.analyst_name || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Additional Comments */}
                {selectedRecord.additional_comments && (
                  <div className="space-y-4 lg:col-span-2">
                    <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Additional Comments</h4>
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-navy-700">
                      <p className="text-sm text-navy-700 dark:text-white">{selectedRecord.additional_comments}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end border-t border-gray-200 pt-3 dark:border-navy-600 sm:mt-6 sm:pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 sm:px-4 sm:py-2 sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && recordToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="relative w-full max-w-md rounded-xl bg-white p-4 dark:bg-navy-800 sm:p-6">
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3 dark:border-navy-600 sm:mb-6 sm:pb-4">
                <div>
                  <h3 className="text-lg font-bold text-navy-700 dark:text-white sm:text-xl">
                    Delete Record
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                    This action cannot be undone
                  </p>
                </div>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700 dark:hover:text-white sm:p-2"
                >
                  <MdClose className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              <div className="mb-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/20">
                    <MdDelete className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-white">
                      Are you sure you want to delete this record?
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      ID: {recordToDelete.id}
                    </p>
                  </div>
                </div>
                <div className="rounded-lg bg-red-50 p-3 dark:bg-red-900/10">
                  <p className="text-xs text-red-700 dark:text-red-300">
                    This will permanently delete the field analysis record and all associated data. This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className="flex-1 rounded-lg bg-red-500 px-3 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default FieldAnalysisRecords;

